import { FC, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

import { AccordionContainer, Accordion } from "@app/shared";
import { Waffles } from "@app/shared/waffles";
import { getComparisonChangeElement } from "@app/shared/utility";

import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSetVersionSummaryType } from "@app/provider-sets/enumerations";
import { ProviderSummaryCountItemModel, ProviderSetVersionSummaryCountModel } from "@app/provider-sets/providerSet";
import { ProviderSetSummaryCountTableData, ProviderSetSummaryDetailProps, createConfig, customSort } from "./ProviderSetDetailHelpers";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

export const ValidationInfo: FC<React.PropsWithChildren<ProviderSetSummaryDetailProps>> = (props: ProviderSetSummaryDetailProps) => {

  const providerSetService = new ProviderSetService();

  const [loading, setLoading] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<ProviderSetVersionSummaryType>(props.activeAccordion);
  const [providersByNpiInspection, setProvidersByNpiInspection] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByNpiInspectionCompare, setProvidersByNpiInspectionCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const [specialtyProvidersCountBreakDown, setSpecialtyProvidersCountBreakDown] = useState<ProviderSetVersionSummaryCountModel>();
  const [specialtyProvidersCountBreakDownCompare, setSpecialtyProvidersCountBreakDownCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByAddressInspection, setProvidersByAddressInspection] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByAddressInspectionCompare, setProvidersByAddressInspectionCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const checkIcon = <QesIcon isDecorative iconName={QesIcons.FontAwesome.Check} width="13" classNames="mr-1" svgStyle={{ bottom: "1px", position: "relative" }} />;

  useEffect(() => {
    setActiveAccordion(ProviderSetVersionSummaryType.Unknown);
  }, [props.providerSetId]);

  useEffect(() => {
    if (!!activeAccordion && activeAccordion !== ProviderSetVersionSummaryType.Unknown)
      fetchSummaryCount(activeAccordion);
  }, [props.version, props.compareVersion]);

  useEffect(() => {
    props.onUpdate(activeAccordion);
  }, [activeAccordion]);

  const createSummaryCountWaffleTable = (summaryType: ProviderSetVersionSummaryType, data: ProviderSummaryCountItemModel[]): ProviderSetSummaryCountTableData[] => {
    return data.map<ProviderSetSummaryCountTableData>(x => {
      let count = (<>{x.totalUniqueRecordCount.toLocaleString("en-US")}</>);

      if (!!x.key && x.totalUniqueRecordCount > 0)
        count = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.version.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.totalUniqueRecordCount.toLocaleString("en-US")}</Link>
        );

      /* eslint-disable sort-keys */
      return ({
        key: x.key,
        description: (!!x.displayName ? x.displayName : "<Not Provided>"),
        descriptionNumeric: (summaryType === ProviderSetVersionSummaryType.SpecCodeCountBreakdown || summaryType === ProviderSetVersionSummaryType.AddressCountBreakdown) ? (parseInt(x.displayName) || 0) : null,
        count: count,
        countNumeric: x.totalUniqueRecordCount,
        versionCount: null,
        compareVersionCount: null,
        change: null,
        changePerc: null,
      });
      /* eslint-enable sort-keys */
    });
  }

  const createSummaryCountCompareWaffleTable = (summaryType: ProviderSetVersionSummaryType, versionData: ProviderSummaryCountItemModel[], compareVersionData: ProviderSummaryCountItemModel[]): ProviderSetSummaryCountTableData[] => {
    const data = versionData.map(x => {
      return ({
        change: 0,
        changePerc: 0,
        compareVersionCount: 0,
        description: (!!x.displayName ? x.displayName : "<Not Provided>"),
        key: x.key,
        versionCount: x.totalUniqueRecordCount ?? 0,
      })
    });

    for (let i = 0; i < compareVersionData.length; i++) {
      const matchIndex = data.findIndex(x => x.key === compareVersionData[i].key);

      if (matchIndex > -1) {
        data[matchIndex].compareVersionCount = compareVersionData[i].totalUniqueRecordCount ?? 0;
      } else {
        data.push({
          change: 0,
          changePerc: 0,
          compareVersionCount: (compareVersionData[i].totalUniqueRecordCount ?? 0),
          description: (!!compareVersionData[i].displayName ? compareVersionData[i].displayName : "<Not Provided>"),
          key: compareVersionData[i].key,
          versionCount: 0,
        })
      }
    }

    return data.map<ProviderSetSummaryCountTableData>(x => {
      let versionCount = (<>{x.versionCount.toLocaleString("en-US")}</>);
      let compareVersionCount = (<>{x.compareVersionCount.toLocaleString("en-US")}</>);

      if (!!x.key && x.versionCount > 0)
        versionCount = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.version.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.versionCount.toLocaleString("en-US")}</Link>
        );

      if (!!x.key && x.compareVersionCount > 0)
        compareVersionCount = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.compareVersion.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.compareVersionCount.toLocaleString("en-US")}</Link>
        );

      const changeNum = x.versionCount - x.compareVersionCount;
      const changeDisp = getComparisonChangeElement(changeNum);

      let changePercNum = 0;
      if (x.compareVersionCount === 0) {
        if (x.versionCount > 0) {
          changePercNum = 100;
        } else {
          changePercNum = 0;
        }
      } else {
        //toFixed accurately rounds the result to 2 decimal places
        //parseFloat removes any trailing 0s
        changePercNum = parseFloat((changeNum / x.compareVersionCount * 100).toFixed(2));
      }
      const changePercDisp = getComparisonChangeElement(changePercNum, (num: number) => num + "%");

      /* eslint-disable sort-keys */
      return ({
        key: x.key,
        description: x.description,
        descriptionNumeric: (summaryType === ProviderSetVersionSummaryType.SpecCodeCountBreakdown || summaryType === ProviderSetVersionSummaryType.AddressCountBreakdown) ? (parseInt(x.description) || 0) : null,
        count: null,
        compareVersionCount: compareVersionCount,
        compareVersionCountNumeric: x.compareVersionCount,
        versionCount: versionCount,
        versionCountNumeric: x.versionCount,
        change: changeDisp,
        changeNumeric: changeNum,
        changePerc: changePercDisp,
        changePercNumeric: changePercNum
      });
      /* eslint-enable sort-keys */
    });
  }

  const providersByNpiInspectionBody = useMemo(() => {
    const columnConfig = createConfig("NPI Inspection", props);

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!providersByNpiInspection && !!props.compareVersion && !!providersByNpiInspectionCompare)
      waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.ByNPIInspection, providersByNpiInspection.summaryCount, providersByNpiInspectionCompare.summaryCount);
    else if (!!providersByNpiInspection)
      waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.ByNPIInspection, providersByNpiInspection.summaryCount);

    return (
      <Waffles
        columns={columnConfig}
        customSort={customSort}
        data={waffleData}
        fullWidth={true}
        loading={loading}
        testId="provider-detail-by-npi-inspection-waffles-table"
      />
    );
  }, [providersByNpiInspection, loading]);

  const providersBySpecialtyInspectionBody = useMemo(() => {
    const columnConfig = createConfig("# of Specialties", props, "# of Providers");

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!specialtyProvidersCountBreakDown && !!props.compareVersion && !!specialtyProvidersCountBreakDownCompare)
      waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.SpecCodeCountBreakdown, specialtyProvidersCountBreakDown.summaryCount, specialtyProvidersCountBreakDownCompare.summaryCount);
    else if (!!specialtyProvidersCountBreakDown)
      waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.SpecCodeCountBreakdown, specialtyProvidersCountBreakDown.summaryCount);

    waffleData = waffleData.sort((a, b) => parseInt(b.description) - parseInt(a.description));

    return (
      <Waffles
        columns={columnConfig}
        customSort={customSort}
        data={waffleData}
        fullWidth={true}
        loading={loading}
        testId="provider-detail-specialty-breakdown-waffles-table"
      />
    );
  }, [specialtyProvidersCountBreakDown, loading]);

  const providersByAddressInspectionBody = useMemo(() => {
    const columnConfig = createConfig("# of Addresses", props, "# of Providers");

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!providersByAddressInspection && !!props.compareVersion && !!providersByAddressInspectionCompare)
      waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.AddressCountBreakdown, providersByAddressInspection.summaryCount, providersByAddressInspectionCompare.summaryCount);
    else if (!!providersByAddressInspection)
      waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.AddressCountBreakdown, providersByAddressInspection.summaryCount);

    waffleData = waffleData.sort((a, b) => parseInt(b.description) - parseInt(a.description));

    return (
      <Waffles
        columns={columnConfig}
        customSort={customSort}
        data={waffleData}
        fullWidth={true}
        loading={loading}
        testId="provider-set-detail-by-address-inpsection-waffles-table"
      />
    );
  }, [providersByAddressInspection, loading]);

  const fetchSummaryCount = async (summaryType: ProviderSetVersionSummaryType) => {
    setLoading(true);

    let setProviders = null;
    let setProvidersCompare = null;
    switch (summaryType) {
      case ProviderSetVersionSummaryType.SpecCodeCountBreakdown: {
        setProviders = setSpecialtyProvidersCountBreakDown;
        setProvidersCompare = setSpecialtyProvidersCountBreakDownCompare;
        setActiveAccordion(summaryType);
        break;
      }
      case ProviderSetVersionSummaryType.AddressCountBreakdown: {
        setProviders = setProvidersByAddressInspection;
        setProvidersCompare = setProvidersByAddressInspectionCompare;
        setActiveAccordion(summaryType);
        break;
      }
      case ProviderSetVersionSummaryType.ByNPIInspection: {
        setProviders = setProvidersByNpiInspection;
        setProvidersCompare = setProvidersByNpiInspectionCompare;
        setActiveAccordion(summaryType);
        break;
      }
      default: {
        return;
      }
    }

    setProviders(await providerSetService.getProviderSetVersionSummaryCount(props.providerSetId, props.version.providerSetVersionId.toString(), summaryType));
    if (!!props.compareVersion) {
      setProvidersCompare(await providerSetService.getProviderSetVersionSummaryCount(props.providerSetId, props.compareVersion.providerSetVersionId.toString(), summaryType));
    } else {
      setProvidersCompare(null);
    }

    setLoading(false);
  }

  return (
    <AccordionContainer>
      <Accordion title={<>{checkIcon}NPI Inspection</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.ByNPIInspection} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.ByNPIInspection)} testId="validation-info__npi-inspection-accordion">
        {providersByNpiInspectionBody}
      </Accordion>
      <Accordion title={<>{checkIcon}Specialty Inspection</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.SpecCodeCountBreakdown} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.SpecCodeCountBreakdown)} testId="validation-info__specialty-inspection-accordion">
        {providersBySpecialtyInspectionBody}
      </Accordion>
      <Accordion title={<>{checkIcon}Address Inspection</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.AddressCountBreakdown} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.AddressCountBreakdown)} testId="validation-info__address-inspection-accordion">
        {providersByAddressInspectionBody}
      </Accordion>
    </AccordionContainer>
  );
}